import { type ReadonlyVec2, type ReadonlyVec4 } from 'gl-matrix';

import { aPersonCanUseWhiteboards } from '@canvas/capability-summaries';
import {
  CustomerImpact,
  EventClassification,
  captureMessageForCapability,
} from '@canvas/sentry-react';
import { type Region } from '@canvas/vector-types';

import { type Bounds } from '../bounds';

import {
  type Box,
  boxWidth,
  boxHeight,
  boxCenterX,
  boxCenterY,
  boxCreate,
  type ReadonlyBox,
} from './box';

/**
 * Turns a Region into a Box
 * Note that the zero-point of a Region is in the centre
 * @param region
 * @returns Box
 */
export function regionToBox(region: Region): Box {
  return {
    left: region.x - region.width / 2,
    right: region.x + region.width / 2,
    top: region.y - region.height / 2,
    bottom: region.y + region.height / 2,
  };
}

/**
 * Turns a Box into a Region
 * @param region
 * @returns Box
 */
export function boxToRegion(box: ReadonlyBox, output?: Region): Region {
  const result: Region = output ?? { x: 0, y: 0, width: 0, height: 0 };

  result.x = boxCenterX(box);
  result.y = boxCenterY(box);
  result.width = boxWidth(box);
  result.height = boxHeight(box);

  return result;
}

/**
 * Converts a Bounds object (with topLeft and bottomRight coordinates) to a Box object
 * (with top, right, bottom, left edges).
 *
 * @param bounds - The bounds to convert, containing topLeftX, topLeftY, bottomRightX, bottomRightY
 * @returns A Box object with top, right, bottom, left properties
 *
 * @example
 * const bounds = { topLeftX: 10, topLeftY: 20, bottomRightX: 100, bottomRightY: 200 };
 * const box = boundsToBox(bounds);
 * // Returns: { top: 20, right: 100, bottom: 200, left: 10 }
 */
export const boundsToBox = (bounds: Bounds) => {
  return {
    top: bounds.topLeftY,
    right: bounds.bottomRightX,
    bottom: bounds.bottomRightY,
    left: bounds.topLeftX,
  };
};

export type Position = {
  x: number;
  y: number;
};

export type Size = {
  width: number;
  height: number;
};

/**
 * Creates a Box from a Position {x, y} and a Size {width, height}
 * Note that here the zero-point of a Position is the centre-point of a Box
 * @param position
 * @param size
 * @returns Box
 */
export function positionAndSizeToBox(position: Position, size: Size): Box {
  if (isNaN(position.x) || isNaN(position.y)) {
    captureMessageForCapability(
      'Element contained NaN values when converting position and size to bounding box.',
      {
        tags: {
          captureLocation: 'positionAndSizeToBox-NaN',
          /**
           * This will likely stop certain elements from being viewable among a lot of other things
           */
          customerImpact: CustomerImpact.CUSTOMERS,
          /**
           * Clear break in contract of this function
           */
          eventClassification: EventClassification.BUG,
        },
      },
      aPersonCanUseWhiteboards,
    );
  }
  return {
    left: (position.x || 0) - size.width / 2,
    right: (position.x || 0) + size.width / 2,
    top: (position.y || 0) - size.height / 2,
    bottom: (position.y || 0) + size.height / 2,
  };
}

/**
 * Creates a Box from a position vec2 and a size vec2
 * Note that here the zero-point of a position is the centre-point of a Box
 * @param position vec2
 * @param size vec2
 * @returns Box
 */
export function positionAndSizeVec2ToBox(
  position: ReadonlyVec2,
  size: ReadonlyVec2,
  output = boxCreate(0, 0, 0, 0),
): Box {
  output.left = position[0] - size[0] / 2;
  output.right = position[0] + size[0] / 2;
  output.top = position[1] - size[1] / 2;
  output.bottom = position[1] + size[1] / 2;

  return output;
}

export function boxFromVec4(boundingBox: ReadonlyVec4): Box {
  return {
    left: boundingBox[0],
    top: boundingBox[1],
    right: boundingBox[2],
    bottom: boundingBox[3],
  };
}

/**
 * Creates a box from a pair of provided corner points, taken to represent diagonally opposite corners of a box
 * @param a the first corner
 * @param b the second corner
 * @returns Box
 */
export function cornerPairToBox(a: ReadonlyVec2, b: ReadonlyVec2): Box {
  const [ax, ay] = a;
  const [bx, by] = b;
  return {
    left: Math.min(ax, bx),
    top: Math.min(ay, by),
    right: Math.max(ax, bx),
    bottom: Math.max(ay, by),
  };
}
